// -----------------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ViewJobDetail, ViewInternDetail } from "../../services/jobs";
import { faArrowLeft, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Icons imports
import twoYearIcon from "../../assets/images/Jobs/2year.png";
import salaryRangeIcon from "../../assets/images/Jobs/lpa.png";
import locationIcon from "../../assets/images/Jobs/location.png";
import degreeIcon from "../../assets/images/Jobs/eduction.png";
import industryTypeIcon from "../../assets/images/Jobs/industry.png";
import jobRoleIcon from "../../assets/images/Jobs/jobRole.png";
import jobDescriptionIcon from "../../assets/images/Jobs/jd.png";
import typeOfIcon from "../../assets/images/Jobs/typeOfJob.png";
import startDateIcon from "../../assets/images/Jobs/startCalen.png";
import deadLineIcon from "../../assets/images/Jobs/deadline.png";
import workingHoursIcon from "../../assets/images/Jobs/workingHours.png";
import payCompensationIcon from "../../assets/images/Jobs/payComp.png";
import startImmediatelyIcon from "../../assets/images/Jobs/immediate.png";
import responsibilitiesIcon from "../../assets/images/Jobs/response.png";
import flexWorkingIcon from "../../assets/images/Jobs/flex.png";
import weeklyPayoutIcon from "../../assets/images/Jobs/weekley.png";
import joiningBonusIcon from "../../assets/images/Jobs/bonus.png";
import resumeIcon from "../../assets/images/Jobs/resume.png";
import portfolioIcon from "../../assets/images/Jobs/portfolio.png";
import EducationIcon from "../../assets/images/Jobs/eduction.png";
import incentiveIcon from "../../assets/images/Jobs/incentive.png";
import fourTosevenLpaIcon from "../../assets/images/Jobs/handUpPay.png";
import overtimeIcon from "../../assets/images/Jobs/overtime.png";
import endDateIcon from "../../assets/images/Jobs/startCalen.png"; // using start calendar for end as well
import nextStepsIcon from "../../assets/images/Jobs/response.png"; // using responsibilities icon for next steps
import qualificationsIcon from "../../assets/images/Jobs/eduction.png"; // using education icon for qualifications

const ViewJobs = () => {
  const navigate = useNavigate();
  const { jobId, internshipId } = useParams(); // Get both possible IDs from params
  const location = useLocation();

  // Determine if we're viewing an internship or job
  const isInternship = location.pathname.includes("internship") || internshipId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [showReasonPopup, setShowReasonPopup] = useState(false);

  // Fetch job or internship data from API
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        // For testing purposes, you can hardcode an ID if not using route params
        const id = isInternship ? internshipId || 1 : jobId || 1;
        console.log(
          `Fetching ${isInternship ? "internship" : "job"} details for id:`,
          id
        );

        // Call the appropriate API based on whether we're viewing a job or internship
        const response = isInternship
          ? await ViewInternDetail(id)
          : await ViewJobDetail(id);

        console.log("API Response:", response);

        if (response && response.data && response.data.statusCode === 200) {
          setData(response.data.result);
        } else {
          console.error("Invalid API response structure:", response);
          setError(
            `Failed to load ${
              isInternship ? "internship" : "job"
            } details. Invalid response format.`
          );
        }
      } catch (error) {
        console.error(
          `Error fetching ${isInternship ? "internship" : "job"} details:`,
          error
        );
        setError(
          `Failed to load ${
            isInternship ? "internship" : "job"
          } details. Please try again later.`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [jobId, internshipId, isInternship]);

  // Parse JSON strings in the data
  const parseJsonString = (jsonString) => {
    if (!jsonString) return [];

    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error("Error parsing JSON string:", error, jsonString);
      return [];
    }
  };

  const formatSalaryRange = (min, max) => {
    if (!min && !max) return "Not specified";
    if (min && !max) return `${min / 100000} LPA`;
    if (!min && max) return `Up to ${max / 100000} LPA`;
    return `${min / 100000} - ${max / 100000} LPA`;
  };

  const toggleFAQ = (index) => {
    if (expandedFAQ === index) {
      setExpandedFAQ(null);
    } else {
      setExpandedFAQ(index);
    }
  };

  // Get status badge
  const getStatusBadge = (status, rejectReason) => {
    let statusText = "Pending";
    let statusClass = "job-status-pending";

    if (status === "1") {
      statusText = "Approved";
      statusClass = "job-status-approved";
    } else if (status === "2") {
      statusText = "Rejected";
      statusClass = "job-status-rejected";
    }

    return (
      <div className="job-status-container">
        <div className={`job-status-badge ${statusClass}`}>{statusText}</div>

        {status === "2" && rejectReason && (
          <>
            <button
              className="view-reason-btn"
              onClick={() => setShowReasonPopup(true)}
            >
              View Reason
            </button>

            {showReasonPopup && (
              <div
                className="reason-popup-overlay"
                onClick={() => setShowReasonPopup(false)}
              >
                <div
                  className="reason-popup"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="reason-popup-header">
                    <h3>Rejection Reason</h3>
                    <button
                      className="close-popup-btn"
                      onClick={() => setShowReasonPopup(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="reason-popup-content">
                    <p>{rejectReason}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Display loading state
  if (loading) {
    return <div className="job-details-container">Loading details...</div>;
  }

  // Display error state
  if (error) {
    return <div className="job-details-container">{error}</div>;
  }

  // Display not found state
  if (!data) {
    return (
      <div className="job-details-container">
        {isInternship ? "Internship" : "Job"} not found
      </div>
    );
  }

  // Field mappings - map internship fields to job fields for consistent rendering
  const fieldMap = {
    positions: isInternship ? data.internshipPositions : data.jobPositions,
    type: isInternship ? data.typesOfinternship : data.typesOfJob,
    location: isInternship ? data.internshipLocation : data.jobLocation,
    description: isInternship
      ? data.internshipDescription
      : data.jobDescription,
    questions: isInternship
      ? parseJsonString(data.questions || "[]").map((q) => ({
          question: q.question,
          id: q.internshipQuesId,
        }))
      : parseJsonString(data.questions || "[]").map((q) => ({
          question: q.question,
          id: q.jobQuesId,
        })),
    status: isInternship ? data.internship_status : data.job_status,
    rejectReason: data.reject_reason,
  };

  // Parse JSON data
  const workingDays = parseJsonString(data.workingDays || "[]");
  const additionalPerks = parseJsonString(data.additionalPerks || "[]");
  const skills = parseJsonString(data.skills || "[]");
  const faqs = parseJsonString(data.faqs || "[]");

  // Handle industry type - it's missing in the API response
  // Using appropriate fallback for missing fields
  const industryTypes = data.industry_type_name
    ? [data.industry_type_name]
    : [];

  // Handle missing fields with N/A or default values
  const displayValue = (value) => (value ? value : "N/A");

  return (
    <div className="job-details-container">
      {/* Page Header with Status Badge */}
      <div className="job-header">
        <button className="job-back-button" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2 className="job-page-title">Detail</h2>
        {getStatusBadge(fieldMap.status, fieldMap.rejectReason)}
      </div>

      {/* Job/Internship Card - Main Info */}
      <div className="job-card">
        <div className="job-card-header">
          <div className="job-title-info">
            <h1 className="job-title">{displayValue(fieldMap.positions)}</h1>
            <p className="job-company">{displayValue(data.companyName)}</p>
          </div>
        </div>

        <div className="job-info-grid">
          <div className="job-info-item">
            <img src={twoYearIcon} alt="Experience" className="icon-enlarged" />
            <span className="job-info-text">
              {data.minExperience && data.maxExperience
                ? `${data.minExperience} - ${data.maxExperience} Years`
                : "Experience: N/A"}
            </span>
          </div>
          <div className="job-info-item">
            <img src={salaryRangeIcon} alt="Salary" className="icon-enlarged" />
            <span className="job-info-text">
              {data.minSalary && data.maxSalary
                ? `₹ ${data.minSalary / 100000} - ${
                    data.maxSalary / 100000
                  } lpa`
                : "Salary: N/A"}
            </span>
          </div>
          <div className="job-info-item">
            <img src={locationIcon} alt="Location" className="icon-enlarged" />
            <span className="job-info-text">
              {displayValue(fieldMap.location)}
            </span>
          </div>
          <div className="job-info-item">
            <img src={degreeIcon} alt="Education" className="icon-enlarged" />
            <span className="job-info-text">
              {displayValue(data.qualificationsRequired)}
            </span>
          </div>
          <div className="job-info-item">
            <img
              src={industryTypeIcon}
              alt="Industry"
              className="icon-enlarged"
            />
            <span className="job-info-text">
              {data.industry_type_name || "Industry Type: N/A"}
            </span>
          </div>
          <div className="job-info-item">
            <img src={jobRoleIcon} alt="Role" className="icon-enlarged" />
            <span className="job-info-text">
              {displayValue(data.designation)}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={jobDescriptionIcon}
            alt={isInternship ? "Internship Description" : "Job Description"}
            className="icon-enlarged"
          />
          <h3 className="job-section-title">
            {isInternship ? "Internship Description" : "Job Description"}
          </h3>
        </div>
        <div className="job-section-content">
          <p>{displayValue(fieldMap.description)}</p>
        </div>
      </div>
      {/* Skills Required */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={qualificationsIcon}
            alt="Skills Required"
            className="icon-enlarged"
          />
          <h3 className="job-section-title">Skills Required</h3>
        </div>
        <div className="job-section-content">
          <div className="job-type-pills">
            {skills.length > 0 ? (
              skills.map((skill, index) => (
                <div key={index} className="job-type-pill">
                  {skill}
                </div>
              ))
            ) : (
              <p>N/A</p>
            )}
          </div>
        </div>
      </div>

      {/* Type of Job/Internship (Merged Section) */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={typeOfIcon}
            alt={isInternship ? "Type of Internship" : "Type of Job"}
            className="icon-enlarged"
          />
          <h3 className="job-section-title">
            {isInternship ? "Type of Internship" : "Type of Job"}
          </h3>
        </div>

        <div className="job-section-content">
          <div className="job-info-subsection">
            <h4 className="job-subsection-title">
              {isInternship ? "Internship Type" : "Job Type"}
            </h4>
            <div className="job-type-pills">
              <div className="job-type-pill">{displayValue(fieldMap.type)}</div>
            </div>
          </div>

          <div className="job-info-subsection">
            <h4 className="job-subsection-title">
              {isInternship ? "Internship Location" : "Job Location"}
            </h4>
            <div className="job-type-pills">
              <div className="job-type-pill">
                {displayValue(fieldMap.location)}
              </div>
            </div>
          </div>

          <div className="job-info-subsection">
            <h4 className="job-subsection-title">Qualification Required</h4>
            <p>{displayValue(data.qualificationsRequired)}</p>
          </div>

          <div className="job-info-subsection">
            <h4 className="job-subsection-title">Type of Interview</h4>
            <div className="job-type-pills">
              <div className="job-type-pill">
                {displayValue(data.typeOfInterview)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Working Days (Merged with Working Hours) */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={workingHoursIcon}
            alt="Working Details"
            className="icon-enlarged"
          />
          <h3 className="job-section-title">Working Details</h3>
        </div>

        <div className="job-section-content">
          <div className="job-info-subsection">
            <h4 className="job-subsection-title">Working Days</h4>
            <div className="job-working-days">
              {workingDays.length > 0 ? (
                workingDays.map((day, index) => (
                  <div key={index} className="job-day-pill">
                    {day}
                  </div>
                ))
              ) : (
                <p>N/A</p>
              )}
            </div>
          </div>

          <div className="job-info-subsection">
            <h4 className="job-subsection-title">Working Hours</h4>
            <p>{displayValue(data.workingHours)} hours per week</p>
          </div>

          {/* Date Information */}
          <div className="job-dates-grid">
            <div className="job-date-item">
              <img
                src={startDateIcon}
                alt="Start Date"
                className="icon-enlarged"
              />
              <div className="job-date-info">
                <span className="job-date-label">Start Date</span>
                <span className="job-date-value">
                  {displayValue(data.startDate)}
                </span>
              </div>
            </div>
            <div className="job-date-item">
              <img src={endDateIcon} alt="End Date" className="icon-enlarged" />
              <div className="job-date-info">
                <span className="job-date-label">End Date</span>
                <span className="job-date-value">
                  {displayValue(data.endDate)}
                </span>
              </div>
            </div>
            <div className="job-date-item">
              <img
                src={deadLineIcon}
                alt="Application Deadline"
                className="icon-enlarged"
              />
              <div className="job-date-info">
                <span className="job-date-label">Application Deadline</span>
                <span className="job-date-value">
                  {displayValue(data.applicationDeadline)}
                </span>
              </div>
            </div>
            <div className="job-date-item">
              <img
                src={startImmediatelyIcon}
                alt="Start Immediately"
                className="icon-enlarged"
              />
              <div className="job-date-info">
                <span className="job-date-label">Start Immediately</span>
                <span className="job-date-value">
                  {displayValue(data.startsImmediately)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pay/Compensation */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={payCompensationIcon}
            alt={isInternship ? "Stipend" : "Pay/Compensation"}
            className="icon-enlarged"
          />
          <h3 className="job-section-title">
            {isInternship ? "Stipend" : "Pay / Compensation"}
          </h3>
        </div>
        <div className="job-pay-options">
          <div className="job-pay-item">
            <img
              src={fourTosevenLpaIcon}
              alt="Salary Range"
              className="icon-enlarged"
            />
            <span>{formatSalaryRange(data.minSalary, data.maxSalary)}</span>
          </div>
          {data.payType && data.payType.toLowerCase().includes("incentive") && (
            <div className="job-pay-item">
              <img
                src={incentiveIcon}
                alt="Incentive"
                className="icon-enlarged"
              />
              <span>Incentive</span>
            </div>
          )}
        </div>
      </div>

      {/* Responsibilities */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={responsibilitiesIcon}
            alt="Responsibilities"
            className="icon-enlarged"
          />
          <h3 className="job-section-title">Responsibilities</h3>
        </div>
        <div className="job-section-content">
          <p>{displayValue(data.responsibilities)}</p>
        </div>
      </div>

      {/* Additional Information */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={flexWorkingIcon}
            alt="Additional Information"
            className="icon-enlarged"
          />
          <h3 className="job-section-title">Additional Information</h3>
        </div>
        <div className="job-perks">
          {additionalPerks.length > 0 ? (
            additionalPerks.map((perk, index) => {
              // Select appropriate icon based on perk name
              let perkIcon = flexWorkingIcon; // default icon
              if (
                perk.toLowerCase().includes("payout") ||
                perk.toLowerCase().includes("weekly")
              ) {
                perkIcon = weeklyPayoutIcon;
              } else if (perk.toLowerCase().includes("bonus")) {
                perkIcon = joiningBonusIcon;
              } else if (perk.toLowerCase().includes("overtime")) {
                perkIcon = overtimeIcon;
              }

              return (
                <div key={index} className="job-perk-item">
                  <img src={perkIcon} alt={perk} className="icon-enlarged" />
                  <span>{perk}</span>
                </div>
              );
            })
          ) : (
            <p>N/A</p>
          )}
        </div>
      </div>

      {/* Next Steps */}
      <div className="job-section">
        <div className="job-section-header">
          <img src={nextStepsIcon} alt="Next Steps" className="icon-enlarged" />
          <h3 className="job-section-title">Next Steps</h3>
        </div>
        <div className="job-section-content">
          <p>{displayValue(data.nextSteps)}</p>
        </div>
      </div>

      {/* Document Required */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={resumeIcon}
            alt="Document Required"
            className="icon-enlarged"
          />
          <h3 className="job-section-title">Document Required</h3>
        </div>
        <div className="job-perks">
          <div className="job-perk-item">
            <img src={resumeIcon} alt="Resume" className="icon-enlarged" />
            <span>Resume</span>
          </div>
          <div className="job-perk-item">
            <img
              src={portfolioIcon}
              alt="Portfolio"
              className="icon-enlarged"
            />
            <span>Portfolio</span>
          </div>
        </div>
      </div>

      {/* Assessment Questions */}
      <div className="job-section">
        <div className="job-section-header">
          <img
            src={qualificationsIcon}
            alt="Assessment Question"
            className="icon-enlarged"
          />
          <h3 className="job-section-title">Assessment Question</h3>
        </div>
        <div className="job-section-content">
          {fieldMap.questions.length > 0 ? (
            fieldMap.questions.map((q, index) => (
              <p key={index}>{q.question}</p>
            ))
          ) : (
            <p>N/A</p>
          )}
        </div>
      </div>

      {/* Frequently Asked Questions */}
      {faqs.length > 0 && (
        <div className="job-faq-section">
          <h3 className="job-faq-title">Frequently Asked questions</h3>
          {faqs.map((faq, index) => (
            <div key={index} className="job-faq-item">
              <div
                className="job-faq-question"
                onClick={() => toggleFAQ(index)}
              >
                <span>Q: {faq.ques}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={expandedFAQ === index ? "expanded" : ""}
                />
              </div>
              {expandedFAQ === index && (
                <div className="job-faq-answer">
                  <p>{faq.ans}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ViewJobs;
